import 'react-multi-carousel/lib/styles.css';

import { gql, request } from 'graphql-request';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import Carousel from 'react-multi-carousel';
import { DotProps } from 'react-multi-carousel/lib/types';

import { Heading } from '@/components/dom/text-elements';
import SpotlightCard from '@/components/global/spotlight-card';
import { IndexCard } from '@/components/products/index-card';
import { IndexSparkRow } from '@/components/products/index-spark-row';
import { FormatUtcDate } from '@/helpers/dates';
import cn from '@/lib/cn';
import { BUILD_GRAPHQL_ENDPOINT, SITE_NAME } from '@/lib/constants';
import { INDEX_CARD_FRAGMENT, INDEX_CARD_FRAGMENT_NAME, IndexCardType } from '@/queries/index-card';
import { INDEX_SPARK_ROW_FRAGMENT, INDEX_SPARK_ROW_FRAGMENT_NAME, IndexSparkRowType } from '@/queries/index-spark-row';
import { SPOTLIGHT_CARD_FRAGMENT, SPOTLIGHT_CARD_FRAGMENT_NAME, SpotlightCardType } from '@/queries/spotlight-card';

const responsive = {
    desktop: {
        breakpoint: { max: 3000, min: 1024 },
        items: 3,
    },
    mobile: {
        breakpoint: { max: 464, min: 0 },
        items: 1,
    },
    superLargeDesktop: {
        // the naming can be any, depends on you.
        breakpoint: { max: 4000, min: 3000 },
        items: 5,
    },
    tablet: {
        breakpoint: { max: 1024, min: 464 },
        items: 2,
    },
};

type Props = {
    spotlightIndex: Optional<SpotlightCardType>;
    newIndexes: Array<IndexCardType>;
    topPerformingIndexes: Array<IndexSparkRowType>;
    bottomPerformingIndexes: Array<IndexSparkRowType>;
};

type IndexPerformanceTableProps = {
    indexes: Array<IndexSparkRowType>;
    title: string;
    className?: string;
};

const IndexPerformanceList = ({ indexes, title, className }: IndexPerformanceTableProps) => {
    return (
        <div className={cn('bg-white rounded-lg px-0 py-6 shadow-md', className)}>
            <Heading
                importance={5}
                className="px-6 mb-3 flex items-center"
            >
                <span>{title}</span>
            </Heading>
            <div className="flex flex-col gap-0">
                {indexes.map((indexData, i) => (
                    <div
                        key={`index-performance-${indexData.symbol}-${i}`}
                        className="flex gap-2 items-start hover:bg-brand-navy-light p-3"
                    >
                        <Heading
                            importance={6}
                            className="leading-6 text-center w-6 flex-shrink-0"
                        >
                            {i + 1}
                        </Heading>

                        <IndexSparkRow
                            index={indexData}
                            className="text-sm flex-grow"
                            titleClassName="text-sm"
                            titleLinkClassName="mb-2"
                            graphProps={{ className: 'bg-transparent', height: 50 }}
                            graphClassName="max-w-[100px] bg-brand-gray-light"
                            performanceChangeProps={{ textClassName: 'text-sm' }}
                            timeRangeLabelText="1 Week"
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};

const CustomDot = ({ onClick, index = 0, active }: DotProps) => {
    return (
        <button
            className={cn('rounded-full w-3 h-3 border border-brand-blue-nu mx-1', {
                'bg-brand-blue-nu': active,
                'bg-white': !active,
            })}
            onClick={onClick}
        >
            <span className="sr-only">Item {index + 1}</span>
        </button>
    );
};

const FeaturedIndices = ({ spotlightIndex, newIndexes, topPerformingIndexes, bottomPerformingIndexes }: Props) => {
    return (
        <>
            <Head>
                <title>Indices | {SITE_NAME}</title>
            </Head>
            <div className="px-12 h-full flex gap-6">
                <main className="flex-grow flex-shrink w-3/4">
                    {spotlightIndex && (
                        <SpotlightCard
                            className="mb-12"
                            index={spotlightIndex}
                            hidePortrait={true}
                        />
                    )}

                    <div className="mb-12 relative">
                        <Heading
                            importance={4}
                            className="mb-6 flex items-center text-brand-black"
                        >
                            <span>New and Noteworthy</span>
                        </Heading>

                        <Carousel
                            responsive={responsive}
                            itemClass="px-3"
                            dotListClass="!left-auto !bottom-auto !top-3 !justify-end"
                            showDots={true}
                            arrows={false}
                            renderDotsOutside={true}
                            customDot={<CustomDot />}
                        >
                            {newIndexes.map((indexData) => (
                                <IndexCard
                                    key={`index-card-${indexData.id}`}
                                    index={indexData}
                                />
                            ))}
                        </Carousel>
                    </div>
                </main>

                <aside className="hidden xl:block w-1/4 flex-grow-0 flex-shrink-0">
                    <IndexPerformanceList
                        indexes={topPerformingIndexes}
                        title="Top 10 of the Week"
                        className="mb-8"
                    />

                    <IndexPerformanceList
                        indexes={bottomPerformingIndexes}
                        title="Bottom 10 of the Week"
                    />
                </aside>
            </div>
        </>
    );
};

export const getServerSideProps: GetServerSideProps = async ({ res }) => {
    // cache for 12 hours on serverside this data doesn't need fresh and the request is expensive
    res.setHeader('Cache-Control', 'public, s-maxage=43200, stale-while-revalidate=21600');

    // Three months ago
    const performanceStartDate = new Date();
    performanceStartDate.setMonth(performanceStartDate.getMonth() - 3);

    // One week ago, plus a day, since we can't count on today's results
    const topStartDate = new Date();
    topStartDate.setDate(topStartDate.getDate() - 8);

    const variables = {
        oneWeekAgo: FormatUtcDate(topStartDate, 'yyyy-MM-dd'),
        spotlightIndexSymbol: 'AISPS',
        threeMonthsAgo: FormatUtcDate(performanceStartDate, 'yyyy-MM-dd'),
    };

    const query = gql`
        ${INDEX_CARD_FRAGMENT}
        ${INDEX_SPARK_ROW_FRAGMENT}
        ${SPOTLIGHT_CARD_FRAGMENT}
        query getIndexes($threeMonthsAgo: Date!, $oneWeekAgo: Date!, $spotlightIndexSymbol: String!) {
            spotlightIndex: index(symbol: $spotlightIndexSymbol) {
                ...${SPOTLIGHT_CARD_FRAGMENT_NAME}
            }
            notableIndexes: indexes(isNotable: true, hasSnapshotsOnly: true) {
                ...${INDEX_CARD_FRAGMENT_NAME}
            }
            rankedIndexes(minAsOfDate: $oneWeekAgo, topCount: 10, bottomCount: 10) {
                top {
                    ...${INDEX_SPARK_ROW_FRAGMENT_NAME}
                }
                bottom {
                    ...${INDEX_SPARK_ROW_FRAGMENT_NAME}
                }
            }
        }
    `;

    const data = await request(BUILD_GRAPHQL_ENDPOINT, query, variables);

    const { spotlightIndex } = data;

    const notableIndexes: Array<IndexCardType> = (data.notableIndexes ?? []).filter(
        (index: IndexCardType) => index.threeMonthDailySnapshots.length > 0
    );

    // TODO: actually determine these with some heuristic
    const newIndexes = [...notableIndexes].sort(() => 0.5 - Math.random()).slice(0, 9);

    const topPerformingIndexes = data.rankedIndexes.top ?? [];
    const bottomPerformingIndexes = data.rankedIndexes.bottom ?? [];

    return {
        props: {
            bottomPerformingIndexes,
            newIndexes,
            spotlightIndex,
            topPerformingIndexes,
        },
    };
};

FeaturedIndices.layout = 'redesign';

export default FeaturedIndices;
